/**
 * Assembles the final column array for a DataTable
 * Handles special columns (selection, edit actions) and maintains proper order
 * When both edit and custom actions exist, they are rendered as adjacent columns
 * styled to visually appear as one merged column
 *
 * @param {Object} params
 * @param {Array} params.definedColumns - Array of created column components
 * @param {Array} params.columnDefinitions - Original column definitions
 * @param {string} params.selectionMode - Selection mode ('multiple', 'single', or null)
 * @param {boolean} params.allowRowEdits - Whether row editing is allowed
 * @param {Object} params.Columns - Column components object (MultipleSelectColumn, EditActionColumn, ActionsColumn)
 * @param {string} params.dataKey - Primary key field name
 * @param {*} params.editingRowKey - Currently editing row's key value (used as dependency to trigger re-creation)
 * @param {Object} params.editingRowKeyRef - React ref containing currently editing row's key value
 * @param {Object} params.editingRowKeysSetRef - React ref containing Set of all rows being edited
 * @param {Object} params.expandedRows - Current expanded rows state
 * @param {Function} params.onRowToggle - Handler to toggle row expansion
 * @returns {Array} Assembled column array
 */
export const assembleColumns = ({
    definedColumns,
    columnDefinitions,
    selectionMode,
    allowRowEdits,
    Columns,
    dataKey,
    editingRowKey,
    editingRowKeyRef,
    editingRowKeysSetRef,
    expandedRows,
    onRowToggle
}) => {
    // Find if there's a custom actions column
    const actionsColumnIndex = columnDefinitions.findIndex(col => col.columnType === 'actions')
    const hasCustomActions = actionsColumnIndex !== -1
    const customActionsTemplate = hasCustomActions ? columnDefinitions[actionsColumnIndex].template : null

    return [
        // Multiple selection column (if enabled)
        ...(selectionMode === 'multiple' ? [Columns.MultipleSelectColumn()] : []),
        // Non-action columns
        ...definedColumns.filter((_, index) => columnDefinitions[index].columnType !== 'actions'),
        // Edit action column (if enabled)
        ...(allowRowEdits ? [Columns.EditActionColumn()] : []),
        // Custom action column - styled to merge with edit column if both exist
        ...(hasCustomActions ? [Columns.ActionsColumn({
            template: customActionsTemplate,
            mergeWithEdit: allowRowEdits,
            dataKey,
            editingRowKey,
            editingRowKeyRef,
            editingRowKeysSetRef,
            expandedRows,
            onRowToggle
        })] : [])
    ]
}
